import axios from 'axios';
import { logger, logError } from '@digiwedge/logger';
async function retry(fn, retries = 3, delayMs = 500, backoff = 1.5) {
    let attempt = 0;
    let currentDelay = delayMs;
    while (true) {
        try {
            return await fn();
        }
        catch (error) {
            if (attempt++ >= retries - 1) {
                throw error;
            }
        }
        await new Promise((resolve) => setTimeout(resolve, currentDelay));
        currentDelay *= backoff;
    }
}
function adaptTokenResponse(rawResponse) {
    return {
        accessToken: (rawResponse.access_token || rawResponse['accessToken'] || ''),
        tokenType: (rawResponse.token_type || rawResponse['tokenType'] || ''),
        expiresIn: (rawResponse.expires_in || rawResponse['expiresIn'] || 0),
        // Optionally include refreshToken or idToken if present:
        ...(rawResponse.refresh_token ? { refreshToken: rawResponse.refresh_token } : {}),
        ...(rawResponse.id_token ? { idToken: rawResponse.id_token } : {}),
    };
}
/**
 * Validates the structure of the OAuth token response.
 *
 * @param tokenResponse - The token response to validate.
 * @returns True if the structure is valid, false otherwise.
 */
function validateTokenResponse(tokenResponse) {
    return (typeof tokenResponse.accessToken === 'string' &&
        typeof tokenResponse.tokenType === 'string' &&
        typeof tokenResponse.expiresIn === 'number');
}
/**
 * Fetches an OAuth 2.0 access token using the client credentials grant type.
 *
 * @param config - OAuth client configuration including optional `authMethod`.
 * @returns A Promise that resolves to the access token and its metadata.
 */
export async function getClientCredentialsToken(config) {
    logger.info('🔑 Requesting OAuth token using client credentials.', {
        tokenUrl: config.tokenUrl,
        clientId: config.clientId,
        scope: config.scope,
        authMethod: config.authMethod ?? 'body',
    });
    // Build the request body
    const requestBody = new URLSearchParams({
        grant_type: 'client_credentials',
        ...(config.scope && { scope: config.scope.join(' ') }),
    });
    let requestOptions = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...(config.customHeaders || {}),
        },
    };
    if (config.authMethod === 'basicAuth') {
        // Use Basic Auth header for credentials
        requestOptions = {
            ...requestOptions,
            auth: {
                username: config.clientId,
                password: config.clientSecret,
            },
        };
    }
    else {
        // Include credentials in the request body
        requestBody.set('client_id', config.clientId);
        requestBody.set('client_secret', config.clientSecret);
    }
    try {
        return await retry(async () => {
            logger.debug('🚀 Sending token request to OAuth server.', {
                tokenUrl: config.tokenUrl,
            });
            const response = await axios.post(config.tokenUrl, requestBody.toString(), requestOptions);
            // Log the full raw response for debugging
            logger.debug('🟢 Raw token response from API:', response.data);
            // Adapt the response to our expected format
            const adaptedResponse = adaptTokenResponse(response.data);
            if (!validateTokenResponse(adaptedResponse)) {
                const errorMessage = '❌ Invalid OAuth token response structure.';
                logError(logger, errorMessage, { rawResponse: response.data });
                throw new Error(errorMessage);
            }
            logger.info('✅ OAuth token successfully retrieved.', {
                expiresIn: adaptedResponse.expiresIn,
            });
            return adaptedResponse;
        }, 3, 500, 1.5); // Retries: 3 times, 500ms initial delay, backoff multiplier 1.5
    }
    catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            logger.error('❌ Token request failed.', {
                status: error.response.status,
                statusText: error.response.statusText,
                headers: error.response.headers,
                data: error.response.data,
            });
        }
        else {
            logger.error('❌ Token request failed with an unknown error.', { error });
        }
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred.';
        const errorContext = {
            tokenUrl: config.tokenUrl,
            clientId: config.clientId,
            scope: config.scope?.join(' '),
            error: errorMessage,
            response: axios.isAxiosError(error) && error.response ? error.response.data : undefined,
        };
        logError(logger, 'Failed to retrieve OAuth token', errorContext);
        return undefined;
    }
}
/**
 * Refreshes an OAuth 2.0 access token using the refresh token grant type.
 *
 * @param refreshToken - The refresh token for acquiring a new access token.
 * @param config - OAuth client configuration, which may include `authMethod`.
 * @returns A Promise that resolves to the new access token and its metadata.
 */
export async function refreshAccessToken(refreshToken, config) {
    logger.info('Refreshing OAuth token.', {
        tokenUrl: config.tokenUrl,
        clientId: config.clientId,
        authMethod: config.authMethod ?? 'body',
    });
    const requestBody = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
    });
    let requestOptions = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...(config.customHeaders || {}),
        },
    };
    if (config.authMethod === 'basicAuth') {
        requestOptions = {
            ...requestOptions,
            auth: {
                username: config.clientId,
                password: config.clientSecret,
            },
        };
    }
    else {
        requestBody.set('client_id', config.clientId);
        requestBody.set('client_secret', config.clientSecret);
    }
    try {
        return await retry(async () => {
            logger.debug('Sending refresh token request to OAuth server.', {
                tokenUrl: config.tokenUrl,
            });
            const response = await axios.post(config.tokenUrl, requestBody.toString(), requestOptions);
            // Log the raw response for debugging purposes
            logger.debug('Raw refresh token response:', response.data);
            const adaptedResponse = adaptTokenResponse(response.data);
            if (!validateTokenResponse(adaptedResponse)) {
                const errorMessage = 'Invalid OAuth token response structure.';
                logError(logger, errorMessage, { rawResponse: response.data });
                throw new Error(errorMessage);
            }
            logger.info('OAuth token successfully refreshed.', {
                expiresIn: adaptedResponse.expiresIn,
            });
            return adaptedResponse;
        }, 3, 500, 1.5);
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred.';
        const errorContext = {
            tokenUrl: config.tokenUrl,
            clientId: config.clientId,
            refreshToken,
            error: errorMessage,
            response: axios.isAxiosError(error) && error.response ? error.response.data : undefined,
        };
        logError(logger, 'Failed to refresh OAuth token', errorContext);
        return undefined;
    }
}
//# sourceMappingURL=oauthUtils.js.map